
import { Modal } from 'bootstrap';
import { defineComponent, inject, ref } from 'vue';
import { useStore } from 'vuex';
import { Select, Image } from '@/components/input-elements';
import InnerLoader from '@/components/InnerLoader.vue';
import { Apollo, Notify } from '@/core/services';
import { useI18n } from 'vue-i18n';
import { Actions } from '@/store/enums/StoreEnums';
import { CREATE_PROMOTION, UPDATE_PROMOTION, CREATE_SERVICE_PROMOTION, UPDATE_SERVICE_PROMOTION } from '@/modules/system/promotions/graphql/Mutations';
import { GET_CUSTOMER_DROPDOWN } from '@/modules/customer/customers/graphql/Queries';
import { GET_CUSTOMER_GROUP } from '@/modules/customer/customer-groups/graphql/Queries';

interface PromotionFormData {
    id: number | null;
    type: string;
    title: string;
    message: string;
    url: string;
    audience_type: string;
    customer_ids: number[];
    customer_group_ids: number[];
    category_ids: number[];
    starts_at: string;
    ends_at: string;
    status: boolean;
}

const emptyForm = (): PromotionFormData => ({
    id: null,
    type: 'PRODUCT',
    title: '',
    message: '',
    url: '',
    audience_type: 'ALL',
    customer_ids: [],
    customer_group_ids: [],
    category_ids: [],
    starts_at: '',
    ends_at: '',
    status: true
});

export default defineComponent({
    name: 'promotion-form',
    components: {
        Select,
        Image,
        InnerLoader
    },
    props: {
        promotionType: {
            type: String,
            default: 'PRODUCT'
        }
    },
    setup(props) {
        const loader = ref(false);
        const modal: any = ref();
        const i18n = useI18n();
        const store = useStore();
        const emitter: any = inject('emitter');
        const promotionForm = ref<null | HTMLFormElement>(null);
        const formData = ref<PromotionFormData>(emptyForm());
        const previewImage = ref([]) as Record<any, any>;
        const customers = ref([]) as Record<any, any>;
        const customerGroups = ref([]) as Record<any, any>;
        const optionsLoaded = ref(false);
        const audienceOptions = ref([
            { label: 'message.ALL_CUSTOMERS', value: 'ALL' },
            { label: 'message.SPECIFIC_CUSTOMERS', value: 'CUSTOMERS' },
            { label: 'message.CUSTOMER_GROUPS', value: 'CUSTOMER_GROUPS' }
        ]);
        const textRules = [
            {
                required: true,
                message: `${i18n.t('message.THIS_FIELD')} ${i18n.t('message.IS_REQUIRED')}`,
                trigger: 'blur'
            }
        ];
        const dateRules = [
            {
                required: true,
                message: `${i18n.t('message.THIS_FIELD')} ${i18n.t('message.IS_REQUIRED')}`,
                trigger: 'change'
            }
        ];
        const shopUrl = String(process.env.VUE_APP_GEP_SHOP || '').replace(/\/$/, '');
        const translatedShopUrl = i18n.t('message.ONLY_SHOP_URL_ALLOWED');
        const shopUrlHint = `${translatedShopUrl && translatedShopUrl !== 'message.ONLY_SHOP_URL_ALLOWED' ? translatedShopUrl : 'Only links from this shop are allowed'}: ${shopUrl}`;
        const urlRules = [
            {
                validator: (_rule, value, callback) => {
                    const url = String(value || '').trim();
                    if (!url) {
                        callback();
                        return;
                    }
                    try {
                        const shop = new URL(shopUrl);
                        const given = new URL(url);
                        if (given.protocol === shop.protocol && given.host === shop.host) {
                            callback();
                            return;
                        }
                    } catch (error) {
                        callback(new Error(shopUrlHint));
                        return;
                    }
                    callback(new Error(shopUrlHint));
                },
                trigger: 'blur'
            }
        ];

        const handleChange = async (event: Record<any, any>) => {
            loader.value = true;
            try {
                const media = event.target.files;
                const response = await store.dispatch(Actions.CREATE_MEDIA, media);
                const images = JSON.parse(response);
                previewImage.value = [];
                images.forEach(element => {
                    previewImage.value.push({ src: element.asset_path, id: element.id });
                });
            } finally {
                event.target.value = '';
                loader.value = false;
            }
        };

        const removeMedia = (index: number, mediaId: number) => {
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = true;
                    const resp = await store.dispatch(Actions.REMOVE_MEDIA, mediaId);
                    if (resp == 'true') {
                        previewImage.value.splice(index, 1);
                        Notify.success(i18n.t('message.MEDIA_REMOVED_SUCCESSFULLY'));
                    }
                    loader.value = false;
                })
                .catch(() => {
                    loader.value = false;
                });
        };

        const showMedia = async (num: number) => {
            await store.dispatch(Actions.TRIGGER_ACTION, {
                formData: {
                    previewImage: previewImage.value,
                    visible: true,
                    index: num
                },
                type: 'imageProps'
            });
        };

        const startOfToday = () => {
            const date = new Date();
            date.setHours(0, 0, 0, 0);
            return date.getTime();
        };

        const disabledDate = (time: Date) => time.getTime() < startOfToday();

        const onAudienceChange = () => {
            formData.value.customer_ids = [];
            formData.value.customer_group_ids = [];
        };

        const disabledEndDate = (time: Date) => {
            if (disabledDate(time)) {
                return true;
            }
            if (!formData.value.starts_at) {
                return false;
            }
            const start = new Date(`${formData.value.starts_at}T00:00:00`);
            return time.getTime() < start.getTime();
        };

        const loadOptions = async () => {
            if (optionsLoaded.value) {
                return;
            }
            loader.value = true;
            const [customerResult, groupResult] = await Promise.all([
                Apollo.query({
                    query: GET_CUSTOMER_DROPDOWN,
                    variables: { page: 1, limit: 10000 },
                    fetchPolicy: 'network-only'
                }),
                Apollo.query({
                    query: GET_CUSTOMER_GROUP,
                    variables: { page: 1, limit: 10000 },
                    fetchPolicy: 'network-only'
                })
            ]);

            customers.value = (customerResult.data?.customers_dropdown?.data || [])
                .filter(item => item?.user?.name)
                .map(item => ({
                    label: item.user.name,
                    value: item.id
                }));
            customerGroups.value = (groupResult.data?.customer_groups?.data || []).map(item => ({
                label: item.name,
                value: item.id
            }));
            optionsLoaded.value = true;
            loader.value = false;
        };

        const openModal = async () => {
            try {
                await loadOptions();
            } catch (error) {
                loader.value = false;
            }
            modal.value = new Modal(document.getElementById('modal_promotion'));
            modal.value.show();
        };

        const submitForm = () => {
            promotionForm.value?.validate(async (valid: boolean) => {
                if (!valid) {
                    Notify.error(i18n.t('message.FILL_ALL_MISSING_FIELDS'));
                    return;
                }
                if (formData.value.audience_type === 'CUSTOMERS' && formData.value.customer_ids.length === 0) {
                    Notify.error(i18n.t('message.SELECT_AT_LEAST_ONE_CUSTOMER'));
                    return;
                }
                if (formData.value.audience_type === 'CUSTOMER_GROUPS' && formData.value.customer_group_ids.length === 0) {
                    Notify.error(i18n.t('message.SELECT_AT_LEAST_ONE_CUSTOMER_GROUP'));
                    return;
                }
                const isService = formData.value.type === 'SERVICE';
                if (!isService && previewImage.value.length === 0) {
                    Notify.error(`${i18n.t('message.IMAGE')} ${i18n.t('message.IS_REQUIRED')}`);
                    return;
                }
                if (formData.value.starts_at && formData.value.ends_at && formData.value.ends_at < formData.value.starts_at) {
                    Notify.error(i18n.t('message.END_DATE_BEFORE_START_DATE'));
                    return;
                }

                const audience = {
                    audience_type: formData.value.audience_type,
                    status: formData.value.status,
                    starts_at: formData.value.starts_at || null,
                    ends_at: formData.value.ends_at || null,
                    customer_ids: formData.value.audience_type === 'CUSTOMERS' ? formData.value.customer_ids : [],
                    customer_group_ids: formData.value.audience_type === 'CUSTOMER_GROUPS' ? formData.value.customer_group_ids : []
                };
                const payload = isService
                    ? {
                          title: formData.value.title.trim(),
                          message: formData.value.message.trim(),
                          audience_type: audience.audience_type,
                          status: audience.status,
                          // starts_at: formData.value.starts_at || null,
                          // ends_at: formData.value.ends_at || null,
                          customer_ids: audience.customer_ids,
                          customer_group_ids: audience.customer_group_ids
                      }
                    : {
                          type: formData.value.type,
                          image: previewImage.value[0].id,
                          url: formData.value.url?.trim() || null,
                          ...audience
                      };
                const isEdit = !!formData.value.id;
                const mutation = isService
                    ? isEdit
                        ? UPDATE_SERVICE_PROMOTION
                        : CREATE_SERVICE_PROMOTION
                    : isEdit
                    ? UPDATE_PROMOTION
                    : CREATE_PROMOTION;
                loader.value = true;
                await Apollo.mutate({
                    mutation,
                    variables: {
                        input: isEdit ? { ...payload, id: formData.value.id } : payload
                    }
                })
                    .then(() => {
                        loader.value = false;
                        modal.value.hide();
                        emitter.emit('reloadPromotions');
                        Notify.success(i18n.t(isEdit ? 'message.RECORD_UPDATED_SUCCESSFULLY' : 'message.RECORD_ADDED_SUCCESSFULLY'));
                    })
                    .catch(() => {
                        loader.value = false;
                    });
            });
        };

        emitter.on('addPromotionAction', async () => {
            formData.value = { ...emptyForm(), type: props.promotionType };
            previewImage.value = [];
            promotionForm.value?.resetFields();
            await openModal();
        });

        emitter.on('editPromotionAction', async (promotion: Record<any, any>) => {
            formData.value = {
                id: promotion.id,
                type: promotion.type || 'PRODUCT',
                title: promotion.title || '',
                message: promotion.message || '',
                url: promotion.url || '',
                audience_type: promotion.audience_type,
                customer_ids: promotion.customer_ids || [],
                customer_group_ids: promotion.customer_group_ids || [],
                category_ids: [],
                starts_at: promotion.starts_at || '',
                ends_at: promotion.ends_at || '',
                status: !!promotion.status
            };
            previewImage.value = promotion.image
                ? [{ id: promotion.image.id, src: promotion.image.file_path }]
                : [];
            await openModal();
        });

        return {
            loader,
            formData,
            previewImage,
            promotionForm,
            customers,
            customerGroups,
            audienceOptions,
            textRules,
            dateRules,
            urlRules,
            shopUrl,
            shopUrlHint,
            handleChange,
            removeMedia,
            showMedia,
            onAudienceChange,
            disabledDate,
            disabledEndDate,
            submitForm
        };
    }
});
